"use client"

import {
  createContext,
  useContext,
  useRef,
  type Ref,
  type RefCallback,
} from "react"

export type DialogPresentation = "dialog" | "drawer"

type DialogPresentationCtx = {
  presentation: DialogPresentation
  showSwipeHandle: boolean
  /** Whether the dialog has finished its open transition. */
  isSettled: () => boolean
}

export const DialogPresentationContext = createContext<DialogPresentationCtx>({
  presentation: "dialog",
  showSwipeHandle: true,
  isSettled: () => true,
})

export function useDialogPresentation() {
  return useContext(DialogPresentationContext)
}

/** Marks the text field a Dialog focuses once it has finished opening. */
const DIALOG_AUTOFOCUS_ATTR = "data-autofocus"

function assignRef<T>(ref: Ref<T> | undefined, node: T | null) {
  if (typeof ref === "function") {
    ref(node)
  } else if (ref) {
    ref.current = node
  }
}

/**
 * Resolve a text field's `autoFocus` (and `ref`) props for use inside a Dialog.
 *
 * React never renders the `autofocus` attribute; it polyfills the prop by
 * calling `focus()` when the element mounts. Inside a Dialog presented as a
 * drawer that is too early: the field mounts while the drawer is still
 * off-screen, so iOS scrolls the page to reveal it (dragging the drawer along)
 * and, since the call is outside the tap, never shows the keyboard. The field
 * is instead marked with `data-autofocus` and `DialogContent` focuses it once
 * the enter transition has completed. A field revealed later, inside an
 * already-open drawer, has no transition to wait for and is focused on mount
 * as React would have. In the centered dialog presentation React's mount-time
 * focus is kept.
 */
export function useDialogAutoFocus<T extends HTMLElement>(
  autoFocus: boolean | undefined,
  ref?: Ref<T>
) {
  const { presentation, isSettled } = useDialogPresentation()
  const didFocusRef = useRef(false)

  if (!autoFocus) {
    return { autoFocus, ref }
  }
  if (presentation !== "drawer") {
    return { autoFocus, ref, [DIALOG_AUTOFOCUS_ATTR]: "" }
  }

  const attach: RefCallback<T> = (node) => {
    assignRef(ref, node)
    // Once only, so later re-renders do not pull focus back to this field.
    if (node && !didFocusRef.current) {
      didFocusRef.current = true
      if (isSettled()) node.focus()
    }
  }

  return { autoFocus: false, [DIALOG_AUTOFOCUS_ATTR]: "", ref: attach }
}

/**
 * First text field inside `root` marked for dialog autofocus — either via
 * `useDialogAutoFocus` or a server-rendered `autofocus` attribute.
 */
export function getAutofocusField(
  root: HTMLElement | null
): HTMLInputElement | HTMLTextAreaElement | null {
  const el = root?.querySelector(`[${DIALOG_AUTOFOCUS_ATTR}], [autofocus]`)
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    return el
  }
  return null
}
